import * as chatApiServices from './services';
import * as legacyServices from './legacyServices';

import { ENABLE_CHAT_API, isFeatureEnabled } from '/b2b/common/helpers/FeatureFlags';
import { MESSAGE_ROLES, PENDING_CONVERSATION_ID, generateMessageId } from './state/reducer';
import React, { createContext, useCallback, useEffect, useMemo, useReducer, useRef } from 'react';
import aiAssistantReducer, { PANEL_MODES, initialState } from './state/reducer';

import { ASSISTANT_MODES } from './constants';
import { ActionTypes } from './state/actions';
import Poll from '/b2b/common/helpers/Poll';
import PropTypes from 'prop-types';
import { clearChatToken } from '/b2b/common/helpers/ChatAuth';
import fromState from '/b2b/common/state/selectors';
import { isEscalationOpen } from './format';
import { validate as isUuid } from 'uuid';
import { useSelector } from 'react-redux';

export const AIAssistantContext = createContext(null);

/*
 * Cadence for polling an open human (Privacy Team) conversation so replies appear
 * without a manual refresh. The provider is the only consumer.
 */
const HUMAN_POLL_INTERVAL_MS = 60_000;
const HUMAN_POLL_TIMEOUT_MS = 15_000;

/*
 * Persists the active conversation across refreshes in one sessionStorage entry
 * ({ customerId, id, mode }); messages are re-fetched on restore. The customerId
 * scopes the value, so a read for a different customer is ignored and conversations
 * never leak across customers.
 */
const STORAGE_KEY = 'osano_ai_active_conversation';

const readStoredConversation = customerId => {
    try {
        const raw = window.sessionStorage?.getItem(STORAGE_KEY);
        const stored = raw ? JSON.parse(raw) : null;
        if (!stored || stored.customerId !== customerId) return null;
        return { id: stored.id, mode: stored.mode };
    } catch {
        return null;
    }
};

const writeStoredConversation = (customerId, { id, mode }) => {
    try {
        window.sessionStorage?.setItem(STORAGE_KEY, JSON.stringify({ customerId, id, mode }));
    } catch {
        // Persistence is best-effort; a failure just means no restore on refresh.
    }
};

const clearStoredConversation = () => {
    try {
        window.sessionStorage?.removeItem(STORAGE_KEY);
    } catch {
        // no-op
    }
};

const AIAssistantProvider = ({ children }) => {
    const [state, dispatch] = useReducer(aiAssistantReducer, initialState);
    const abortControllerRef = useRef(null);
    const loadingConversationsCountRef = useRef(0);
    const pollRef = useRef(null);
    const bootstrappedRef = useRef(false);

    const customerId = useSelector(fromState.Auth.getCustomerId);

    /*
     * Which backend answers. With the flag on the whole data layer is chat-api — the
     * system of record for conversations — reached browser-direct with an
     * auth-service-minted Bearer; with it off, ./legacyServices.js and the webservices
     * assistant, which is everything droppable when the flag goes away. The two modules
     * share one function surface, so this line is the only place that knows which is
     * which; everything below just calls `services`.
     */
    const isChatApiEnabled = useSelector(state => isFeatureEnabled(state, ENABLE_CHAT_API));
    const services = useMemo(
        () => (isChatApiEnabled ? chatApiServices : legacyServices),
        [isChatApiEnabled]
    );

    const setPanelMode = useCallback(mode => {
        dispatch({ type: ActionTypes.SET_PANEL_MODE, payload: mode });
    }, []);

    const setContext = useCallback(ctx => {
        dispatch({ type: ActionTypes.SET_CONTEXT, payload: ctx });
    }, []);

    const loadConversations = useCallback(
        async ({ silent = false, mode, page, pageSize } = {}) => {
            if (!silent) {
                loadingConversationsCountRef.current += 1;
                dispatch({ type: ActionTypes.SET_LOADING_CONVERSATIONS, payload: true });
            }
            try {
                const response = await services.fetchConversations(mode, { page, pageSize });
                const conversations = response?.results || [];
                if (page && page > 1) {
                    dispatch({
                        type: ActionTypes.APPEND_CONVERSATIONS,
                        payload: { mode, conversations },
                    });
                } else {
                    dispatch({
                        type: ActionTypes.SET_CONVERSATIONS,
                        payload: { mode, conversations },
                    });
                }
                return { conversations, hasNextPage: response?.hasNextPage || false };
            } catch (error) {
                if (!silent) {
                    dispatch({
                        type: ActionTypes.SET_ERROR,
                        payload: { message: error.message, errorCode: error.errorCode },
                    });
                }
            } finally {
                if (!silent) {
                    loadingConversationsCountRef.current -= 1;
                    if (loadingConversationsCountRef.current <= 0) {
                        dispatch({ type: ActionTypes.SET_LOADING_CONVERSATIONS, payload: false });
                    }
                }
            }
        },
        [services]
    );

    const loadConversation = useCallback(
        async (conversationId, { silent = false, mode } = {}) => {
            if (!silent) {
                dispatch({ type: ActionTypes.SET_LOADING_CONVERSATION, payload: true });
                dispatch({ type: ActionTypes.CLEAR_ERROR });
            }
            try {
                const conversation = await services.fetchConversationById(conversationId, {
                    mode,
                });
                if (conversation?.messages) {
                    dispatch({
                        type: ActionTypes.SET_MESSAGES,
                        payload: { conversationId, messages: conversation.messages },
                    });
                }
                if (conversation?.messagePagination) {
                    dispatch({
                        type: ActionTypes.SET_MESSAGE_PAGINATION,
                        payload: { conversationId, ...conversation.messagePagination },
                    });
                }
                // Sync conversation metadata (status, title, etc.)
                const {
                    messages: _messages,
                    messagePagination: _pagination,
                    ...metadata
                } = conversation || {};
                if (metadata && Object.keys(metadata).length > 0) {
                    dispatch({
                        type: ActionTypes.UPDATE_CONVERSATION,
                        payload: { conversationId, data: metadata },
                    });
                }
                return conversation;
            } catch (error) {
                if (!silent) {
                    dispatch({
                        type: ActionTypes.SET_ERROR,
                        payload: { message: error.message, errorCode: error.errorCode },
                    });
                }
            } finally {
                if (!silent) {
                    dispatch({ type: ActionTypes.SET_LOADING_CONVERSATION, payload: false });
                }
            }
        },
        [services]
    );

    /*
     * Poll the conversation the Privacy Team owns so their replies appear without a
     * manual refresh — a legacy human-mode thread, or under chat-api a conversation
     * carrying an unresolved escalation (there is no realtime push in v1). Lives in the
     * data layer since it's a freshness concern, not view state. Fires after the first
     * interval (selecting already fetches latest).
     */
    const isPrivacyTeamOwned = isChatApiEnabled
        ? isEscalationOpen(state.activeConversation?.escalation)
        : state.activeConversation?.mode === ASSISTANT_MODES.HUMAN;

    useEffect(() => {
        if (pollRef.current) {
            pollRef.current.abort();
            pollRef.current = null;
        }
        if (!state.activeConversationId || !isPrivacyTeamOwned) {
            return undefined;
        }
        const conversationId = state.activeConversationId;
        const poll = Poll(
            () =>
                loadConversation(conversationId, {
                    silent: true,
                    // The legacy endpoint needs the mode to find a HelpScout thread;
                    // chat-api resolves the conversation by id alone.
                    ...(isChatApiEnabled ? {} : { mode: ASSISTANT_MODES.HUMAN }),
                }),
            {
                delay: HUMAN_POLL_INTERVAL_MS,
                interval: HUMAN_POLL_INTERVAL_MS,
                timeout: HUMAN_POLL_TIMEOUT_MS,
            }
        );
        pollRef.current = poll;
        return () => {
            poll.abort();
            pollRef.current = null;
        };
    }, [state.activeConversationId, isPrivacyTeamOwned, isChatApiEnabled, loadConversation]);

    const loadEarlierMessages = useCallback(
        async conversationId => {
            const pagination = state.messagePagination[conversationId];
            if (!pagination?.hasNext || !pagination?.nextCursor) return;

            dispatch({ type: ActionTypes.SET_LOADING_EARLIER_MESSAGES, payload: true });
            try {
                const result = await services.fetchConversationById(conversationId, {
                    cursor: pagination.nextCursor,
                    pageSize: pagination.pageSize || undefined,
                });
                dispatch({
                    type: ActionTypes.PREPEND_MESSAGES,
                    payload: { conversationId, messages: result.messages || [] },
                });
                if (result.messagePagination) {
                    dispatch({
                        type: ActionTypes.SET_MESSAGE_PAGINATION,
                        payload: { conversationId, ...result.messagePagination },
                    });
                }
            } catch (error) {
                dispatch({
                    type: ActionTypes.SET_ERROR,
                    payload: { message: error.message, errorCode: error.errorCode },
                });
            } finally {
                dispatch({ type: ActionTypes.SET_LOADING_EARLIER_MESSAGES, payload: false });
            }
        },
        [state.messagePagination, services]
    );

    const startConversation = useCallback(() => {
        dispatch({ type: ActionTypes.SET_ACTIVE_CONVERSATION, payload: null });
    }, []);

    const setActiveConversation = useCallback(conversationId => {
        dispatch({ type: ActionTypes.SET_ACTIVE_CONVERSATION, payload: conversationId });
    }, []);

    /*
     * Open a conversation by id: make it active and load its messages. Mode comes
     * from the caller, else the loaded list entry, else the id shape (AI ids are
     * UUIDs; HelpScout/human ids are not). Used instead of routing so navigation
     * works identically in every panel mode.
     */
    // Resolve a conversation's mode: caller-provided, else the loaded list entry,
    // else inferred from the id shape (AI ids are UUIDs; HelpScout/human ids are not).
    // Shared by openConversation and the storage restore so the two can't drift. Under
    // chat-api every id is a UUID, so this always lands on the AI mode it tags them with.
    const resolveConversationMode = useCallback(
        (conversationId, mode) =>
            mode ??
            state.conversations.find(c => c.id === conversationId)?.mode ??
            (isUuid(conversationId) ? ASSISTANT_MODES.AI : ASSISTANT_MODES.HUMAN),
        [state.conversations]
    );

    const openConversation = useCallback(
        (conversationId, { mode } = {}) => {
            if (!conversationId) return;
            dispatch({ type: ActionTypes.SET_ACTIVE_CONVERSATION, payload: conversationId });
            loadConversation(conversationId, {
                mode: resolveConversationMode(conversationId, mode),
            });
        },
        [resolveConversationMode, loadConversation]
    );

    /*
     * Resolve which conversation to open. A deep-link id always opens it — even later
     * in the session — and counts as resolving the initial state, so the storage
     * restore is skipped. The storage restore itself runs at most once; a restored
     * conversation that 404s falls back to a blank chat.
     */
    const bootstrapActiveConversation = useCallback(
        async ({ deepLinkId } = {}) => {
            if (deepLinkId) {
                bootstrappedRef.current = true;
                openConversation(deepLinkId);
                return;
            }

            if (bootstrappedRef.current) return;
            bootstrappedRef.current = true;

            const stored = readStoredConversation(customerId);
            if (!stored?.id) return;

            dispatch({ type: ActionTypes.SET_ACTIVE_CONVERSATION, payload: stored.id });
            const conversation = await loadConversation(stored.id, {
                mode: resolveConversationMode(stored.id, stored.mode),
                silent: true,
            });
            if (!conversation) {
                // Stored conversation is gone — land on a blank chat, not an error.
                dispatch({ type: ActionTypes.SET_ACTIVE_CONVERSATION, payload: null });
                dispatch({ type: ActionTypes.CLEAR_ERROR });
                clearStoredConversation();
            }
        },
        [customerId, openConversation, loadConversation, resolveConversationMode]
    );

    const sendMessage = useCallback(
        async (content, options = {}) => {
            let conversationId = options.conversationId || state.activeConversationId;
            const isNewConversation = !conversationId;
            const existingConversation = state.conversations.find(c => c.id === conversationId);
            const mode = options.mode || existingConversation?.mode || ASSISTANT_MODES.AI;
            const isHumanMode = mode === ASSISTANT_MODES.HUMAN;

            /*
             * Is a reply coming? Two cases say no, and they answer the same way — the
             * message is persisted and the humans on the other side respond in their own
             * time. A legacy human-mode thread is one; under chat-api, a conversation
             * carrying an unresolved escalation is the other: AI turns are paused while
             * the Privacy Team owns it, and the message is mirrored onto their ticket.
             *
             * This is only a prediction — the escalation status is refreshed by a poll, so
             * it can be stale in either direction. The stream reconciles both: onNoReply
             * when a reply we expected never comes, and ensureAssistantPlaceholder below
             * when one arrives on a thread we thought was paused.
             */
            const conversation =
                existingConversation ||
                (conversationId === state.activeConversationId ? state.activeConversation : null);
            const isPaused = isChatApiEnabled && isEscalationOpen(conversation?.escalation);
            const expectsReply = !isHumanMode && !isPaused;
            // chat-api serves both cases from the streaming endpoint; the legacy backend
            // keeps human conversations on its non-streaming path.
            const useStreaming = isChatApiEnabled || !isHumanMode;

            const messageKey = conversationId || PENDING_CONVERSATION_ID;

            const userMessage = {
                id: generateMessageId(),
                role: MESSAGE_ROLES.USER,
                content,
                timestamp: new Date().toISOString(),
            };

            dispatch({
                type: ActionTypes.ADD_MESSAGE,
                payload: { conversationId: messageKey, message: userMessage },
            });

            let assistantMessageId = null;
            /*
             * Opens the reply bubble and the spinner together, first caller wins. Called
             * eagerly below when a reply is expected, so the happy path still gets its
             * instant feedback, and lazily from the stream the moment a reply actually
             * starts. The stream is the source of truth: tokens arriving *are* proof an AI
             * turn ran, and APPEND_TOKEN drops tokens with no assistant bubble to land in,
             * so a stale "paused" guess would otherwise lose the whole reply.
             */
            const ensureAssistantPlaceholder = () => {
                if (assistantMessageId) return;
                assistantMessageId = generateMessageId();
                dispatch({
                    type: ActionTypes.ADD_MESSAGE,
                    payload: {
                        conversationId: conversationId || messageKey,
                        message: {
                            id: assistantMessageId,
                            role: MESSAGE_ROLES.ASSISTANT,
                            content: '',
                            timestamp: new Date().toISOString(),
                        },
                    },
                });
                dispatch({ type: ActionTypes.SET_STREAMING, payload: true });
            };

            if (isNewConversation) {
                dispatch({ type: ActionTypes.SET_ACTIVE_CONVERSATION, payload: messageKey });
            }

            // After SET_ACTIVE_CONVERSATION, which clears isStreaming on its way through.
            if (expectsReply) ensureAssistantPlaceholder();

            dispatch({ type: ActionTypes.CLEAR_ERROR });

            try {
                if (!useStreaming) {
                    // Legacy human conversations use the non-streaming path
                    const response = await services.sendChatMessage(content, conversationId, mode);
                    const returnedId = response?.conversationId;
                    if (isNewConversation && returnedId) {
                        conversationId = returnedId;
                        dispatch({
                            type: ActionTypes.ADOPT_CONVERSATION,
                            payload: { tempId: messageKey, realId: returnedId },
                        });
                    }
                    if (conversationId) {
                        loadConversation(conversationId, { silent: true, mode });
                    }
                } else {
                    // AI conversations use SSE streaming

                    abortControllerRef.current?.abort();
                    abortControllerRef.current = new AbortController();

                    await services.sendChatMessageStream(content, conversationId, {
                        signal: abortControllerRef.current.signal,
                        onConversationId: returnedId => {
                            if (isNewConversation && returnedId && returnedId !== messageKey) {
                                conversationId = returnedId;
                                dispatch({
                                    type: ActionTypes.ADOPT_CONVERSATION,
                                    payload: { tempId: messageKey, realId: returnedId },
                                });
                            }
                        },
                        onMessageId: serverId => {
                            ensureAssistantPlaceholder();
                            if (serverId && assistantMessageId && serverId !== assistantMessageId) {
                                const activeKey = conversationId || messageKey;
                                dispatch({
                                    type: ActionTypes.UPDATE_MESSAGE_ID,
                                    payload: {
                                        conversationId: activeKey,
                                        oldId: assistantMessageId,
                                        newId: serverId,
                                    },
                                });
                                assistantMessageId = serverId;
                            }
                        },
                        onToken: token => {
                            ensureAssistantPlaceholder();
                            const activeKey = conversationId || messageKey;
                            dispatch({
                                type: ActionTypes.APPEND_TOKEN,
                                payload: { conversationId: activeKey, token },
                            });
                        },
                        onCitations: citations => {
                            ensureAssistantPlaceholder();
                            const activeKey = conversationId || messageKey;
                            dispatch({
                                type: ActionTypes.SET_MESSAGE_CITATIONS,
                                payload: {
                                    conversationId: activeKey,
                                    messageId: assistantMessageId,
                                    citations,
                                },
                            });
                        },
                        onDone: () => {
                            dispatch({ type: ActionTypes.SET_STREAMING, payload: false });
                            /*
                             * A reply arrived on a conversation we thought the Privacy Team
                             * still owned, so the server has resolved the escalation and
                             * resumed AI turns. Pull the current state to un-gate the
                             * composer instead of waiting out the poll. The placeholder id
                             * is the proof a reply actually landed — a correctly paused
                             * stream ends through onNoReply and never reaches this.
                             */
                            if (isPaused && assistantMessageId && conversationId) {
                                loadConversation(conversationId, { silent: true });
                            }
                            if (isNewConversation && conversationId) {
                                // Set a provisional title from the user's message so the top bar
                                // and button appear together with the completed response.
                                // The real server-generated title will overwrite this when ready.
                                const provisionalTitle =
                                    content.length > 60
                                        ? content.substring(0, 57) + '...'
                                        : content;
                                dispatch({
                                    type: ActionTypes.UPDATE_CONVERSATION,
                                    payload: {
                                        conversationId,
                                        data: { title: provisionalTitle },
                                    },
                                });
                            }
                        },
                        /*
                         * No AI turn ran: the conversation is with the Privacy Team, so
                         * the message was mirrored onto their ticket instead of answered.
                         * Drop the reply placeholder rather than leave an empty bubble,
                         * and re-fetch so the persisted message carries its server id.
                         */
                        onNoReply: () => {
                            dispatch({ type: ActionTypes.SET_STREAMING, payload: false });
                            if (assistantMessageId) {
                                dispatch({
                                    type: ActionTypes.REMOVE_MESSAGE,
                                    payload: {
                                        conversationId: conversationId || messageKey,
                                        messageId: assistantMessageId,
                                    },
                                });
                                assistantMessageId = null;
                            }
                            if (conversationId) {
                                loadConversation(conversationId, { silent: true });
                            }
                        },
                        onError: error => {
                            dispatch({
                                type: ActionTypes.SET_ERROR,
                                payload: { message: error.message, errorCode: error.errorCode },
                            });
                            dispatch({ type: ActionTypes.SET_STREAMING, payload: false });
                        },
                    });
                }
            } catch (error) {
                if (error.name !== 'AbortError') {
                    dispatch({
                        type: ActionTypes.SET_ERROR,
                        payload: { message: error.message, errorCode: error.errorCode },
                    });
                    if (expectsReply) {
                        dispatch({ type: ActionTypes.SET_STREAMING, payload: false });
                    }
                }
            }

            /*
             * Refresh the list so the new/updated conversation surfaces. A legacy human
             * send can't change that list and already refreshes its own conversation
             * above, so skip the fetch there.
             */
            if (useStreaming) {
                loadConversations({ silent: true, mode: ASSISTANT_MODES.AI });
            }

            return { conversationId: conversationId || messageKey };
        },
        [
            state.activeConversationId,
            state.activeConversation,
            state.conversations,
            state.messages,
            isChatApiEnabled,
            loadConversation,
            loadConversations,
            services,
        ]
    );

    const sendFeedback = useCallback(
        async (messageId, sentiment) => {
            try {
                await services.submitFeedback(state.activeConversationId, messageId, sentiment);
            } catch (error) {
                dispatch({
                    type: ActionTypes.SET_ERROR,
                    payload: { message: error.message, errorCode: error.errorCode },
                });
            }
        },
        [state.activeConversationId, services]
    );

    const setError = useCallback((message, errorCode) => {
        dispatch({ type: ActionTypes.SET_ERROR, payload: { message, errorCode } });
    }, []);

    const clearError = useCallback(() => {
        dispatch({ type: ActionTypes.CLEAR_ERROR });
    }, []);

    const deleteConversation = useCallback(
        async conversationId => {
            await services.deleteConversation(conversationId);
            dispatch({ type: ActionTypes.REMOVE_CONVERSATION, payload: { conversationId } });
        },
        [services]
    );

    const reset = useCallback(() => {
        abortControllerRef.current?.abort();
        // A chat-api token is scoped to one customer, so it must not outlive a reset.
        clearChatToken();
        dispatch({ type: ActionTypes.RESET });
    }, []);

    const escalate = useCallback(
        async (conversationId, payload) => {
            const response = await services.escalateConversation(conversationId, payload);

            /*
             * Under chat-api an escalation *is* a new conversation — it carries the
             * customer's first message and is paused from birth — so the widget switches
             * to it rather than marking the current one escalated. The list refresh brings
             * it in with its escalation attached, which is what puts it under the Privacy
             * Team tab and what the paused banner reads.
             */
            if (isChatApiEnabled) {
                if (response?.conversationId) {
                    await loadConversations({ silent: true, mode: ASSISTANT_MODES.AI });
                    openConversation(response.conversationId);
                }
                return response;
            }

            dispatch({
                type: ActionTypes.ESCALATE_CONVERSATION,
                payload: {
                    conversationId,
                    escalatedAt: response?.escalatedAt || new Date().toISOString(),
                },
            });
            if (response?.helpscoutConversations?.length) {
                // Surface the linked HelpScout tickets on the AI conversation so the
                // inline "Submitted to privacy team" markers can render without a
                // separate fetch.
                dispatch({
                    type: ActionTypes.UPDATE_CONVERSATION,
                    payload: {
                        conversationId,
                        data: { helpscoutConversations: response.helpscoutConversations },
                    },
                });
                // The same array is the authoritative source for the Privacy Team
                // conversation list — use it to insert the newly-created ticket
                // directly, no separate refetch needed.
                [...response.helpscoutConversations].reverse().forEach(conv => {
                    dispatch({
                        type: ActionTypes.PREPEND_CONVERSATION,
                        payload: { ...conv, mode: ASSISTANT_MODES.HUMAN },
                    });
                });
            }
            return response;
        },
        [isChatApiEnabled, loadConversations, openConversation, services]
    );

    /*
     * On customer switch, drop the current conversation so the user lands on a blank
     * chat. The bootstrap guard is left set so persistence stays live for the new
     * customer; the switch can't mis-persist because the write effect below doesn't
     * key on customerId.
     */
    const prevCustomerIdRef = useRef(customerId);
    useEffect(() => {
        if (prevCustomerIdRef.current === customerId) return;
        prevCustomerIdRef.current = customerId;
        reset();
    }, [customerId, reset]);

    /*
     * Keep sessionStorage in sync with the active conversation so a refresh restores
     * it: write a real id, and clear when the chat goes blank (new chat, delete, or
     * customer switch). The transient pending id is ignored. Gated on bootstrap so
     * the pre-restore window doesn't run. customerId is deliberately not a dependency:
     * a switch flips it a render before reset() clears the active conversation, so
     * depending on it would persist the outgoing conversation under the incoming
     * customer.
     */
    useEffect(() => {
        if (!bootstrappedRef.current) return;
        const id = state.activeConversationId;
        if (id === PENDING_CONVERSATION_ID) return;
        if (!id) {
            clearStoredConversation();
            return;
        }
        writeStoredConversation(customerId, { id, mode: state.activeConversation?.mode });
    }, [state.activeConversationId, state.activeConversation?.mode]);

    /*
     * Memoized so consumers re-render only when state (or a callback identity)
     * changes, not on every provider render — the context is app-root and widely
     * subscribed. Callbacks are useCallback-stable, so this effectively tracks state.
     */
    const value = useMemo(
        () => ({
            ...state,
            activeMessagePagination: state.messagePagination[state.activeConversationId] || {
                nextCursor: null,
                hasNext: false,
            },
            activeMessages: state.messages[state.activeConversationId] || [],
            bootstrapActiveConversation,
            clearError,
            deleteConversation,
            escalate,
            // Consumers that render differently per backend read it from here rather
            // than re-selecting the flag (the conversation list, for one).
            isChatApiEnabled,
            loadConversation,
            loadConversations,
            loadEarlierMessages,
            openConversation,
            reset,
            sendFeedback,
            sendMessage,
            setActiveConversation,
            setContext,
            setError,
            setPanelMode,
            startConversation,
        }),
        [
            state,
            bootstrapActiveConversation,
            clearError,
            deleteConversation,
            escalate,
            isChatApiEnabled,
            loadConversation,
            loadConversations,
            loadEarlierMessages,
            openConversation,
            reset,
            sendFeedback,
            sendMessage,
            setActiveConversation,
            setContext,
            setError,
            setPanelMode,
            startConversation,
        ]
    );

    return <AIAssistantContext.Provider value={value}>{children}</AIAssistantContext.Provider>;
};

AIAssistantProvider.propTypes = {
    children: PropTypes.node.isRequired,
};

export { PANEL_MODES };
export default AIAssistantProvider;
